import { Injectable, Signal, Type, signal } from '@angular/core';

export type SidePanelSize = 'large' | 'standard';

export interface SidePanelOpenOptions<TInput extends Record<string, unknown>> {
  badge?: { icon?: string; label: string; };
  component: Type<unknown>;
  id: string;
  inputs?: TInput;
  size?: SidePanelSize;
  title?: string;
}

export class SidePanelRef<TOutput> {
  private dismissed = false;
  private listeners = new Set<(output: TOutput | undefined) => void>();
  private output: TOutput | undefined;

  constructor(
    private onDismissRequest: (output: TOutput | undefined) => void,
  ) {}

  dismiss(output?: TOutput): void {
    this.onDismissRequest(output);
  }

  onDismiss(callback: (output: TOutput | undefined) => void): () => void {
    if (this.dismissed) {
      callback(this.output);
      return () => undefined;
    }
    this.listeners.add(callback);
    return () => this.listeners.delete(callback);
  }

  notifyDismiss(output?: TOutput): void {
    if (this.dismissed) {
      return;
    }
    this.dismissed = true;
    this.output = output;
    this.listeners.forEach((listener) => listener(output));
    this.listeners.clear();
  }
}

export interface SidePanelInstance {
  badge?: { icon?: string; label: string; };
  component: Type<unknown>;
  dismissOutput?: Record<string, unknown>;
  id: string;
  inputs?: Record<string, unknown>;
  ref: SidePanelRef<unknown>;
  size: SidePanelSize;
  title: string | null;
}

@Injectable({
  providedIn: 'root',
})
export class SidePanelService {
  private sidePanelInstances = signal<SidePanelInstance[]>([]);

  get(): Signal<SidePanelInstance[]> {
    return this.sidePanelInstances.asReadonly();
  }

  close<TOutput extends Record<string, unknown> = Record<string, never>>(id: string, output?: TOutput): void {
    this.dismiss(id, output);
  }

  open<TInput extends Record<string, unknown> = Record<string, never>, TOutput extends Record<string, unknown> = Record<string, never>>(options: SidePanelOpenOptions<TInput>): SidePanelRef<TOutput> {
    this.dismiss(options.id);
    const ref = new SidePanelRef<TOutput>((output) => this.dismiss(options.id, output));
    this.sidePanelInstances.update((instances) => [...instances, {
      badge: options.badge,
      component: options.component,
      id: options.id,
      inputs: options.inputs,
      ref: ref as SidePanelRef<unknown>,
      size: options.size ?? 'standard',
      title: options.title ?? null,
    }]);
    return ref;
  }

  setDismissOutput<TOutput extends Record<string, unknown>>(id: string, output: TOutput): void {
    this.sidePanelInstances.update((instances) => instances.map((instance) => {
      if (instance.id !== id) {
        return instance;
      }
      return {
        ...instance,
        dismissOutput: output,
      };
    }));
  }

  setTitle(id: string, title: string): void {
    this.sidePanelInstances.update((instances) => instances.map((instance) => {
      if (instance.id !== id) {
        return instance;
      }
      return {
        ...instance,
        title,
      };
    }));
  }

  private dismiss(id: string, output?: unknown): void {
    const instances = this.sidePanelInstances();
    const instance = instances.find((item) => item.id === id);
    if (!instance) {
      return;
    }
    this.sidePanelInstances.set(instances.filter((item) => item.id !== id));
    instance.ref.notifyDismiss(output ?? instance.dismissOutput);
  }
}
